import { RiTicketLine } from "@remixicon/react";
import Image from "next/image";
import { memo } from "react";
import { cn } from "@/lib/utils";

interface ShowPhotoProps {
	src: string | null | undefined;
	alt: string;
	className?: string;
	priority?: boolean;
	fetchPriority?: "high" | "low" | "auto";
	loading?: "eager" | "lazy";
	sizes?: string;
	onLoad?: () => void;
	rounded?: "sm" | "md" | "lg" | "xl" | undefined;
	objectPosition?:
		| "center"
		| "top"
		| "bottom"
		| "left"
		| "right"
		| "center top"
		| "center bottom";
	objectFit?: "cover" | "contain" | "smart";
	iconSize?: "sm" | "md" | "lg";
}

const roundedClasses = {
	sm: "rounded-sm",
	md: "rounded-md",
	lg: "rounded-lg",
	xl: "rounded-xl",
};

const iconSizeClasses = {
	sm: "h-5 w-5",
	md: "h-6 w-6",
	lg: "h-8 w-8 md:h-10 md:w-10 lg:h-12 lg:w-12",
};

export const ShowPhoto = memo(function ShowPhoto({
	src,
	alt,
	className,
	priority = false,
	fetchPriority = "auto",
	loading = "lazy",
	sizes = "(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw",
	onLoad,
	rounded = "lg",
	objectPosition = "center",
	objectFit = "smart",
	iconSize = "md",
}: ShowPhotoProps) {
	const roundedClass = rounded ? roundedClasses[rounded] : "";
	const iconSizeClass = iconSizeClasses[iconSize];

	// Smart object-fit: always use cover for proper cropping
	const getObjectFit = () => {
		if (objectFit === "smart") {
			return "cover"; // Use cover for proper cropping without letterboxing
		}
		return objectFit;
	};

	if (!src) {
		return (
			<div
				className={cn(
					"flex h-full w-full items-center justify-center bg-muted",
					roundedClass,
					className,
				)}
			>
				<RiTicketLine className={cn("text-muted-foreground", iconSizeClass)} />
			</div>
		);
	}

	return (
		<div
			className={cn(
				"relative h-full w-full overflow-hidden bg-gradient-to-b from-muted/60 to-muted",
				roundedClass,
				className,
			)}
		>
			<Image
				src={src}
				alt={alt}
				fill
				sizes={sizes}
				className={cn(
					`object-${getObjectFit()}`,
					"transition-opacity duration-200",
				)}
				style={{ objectPosition }}
				priority={priority}
				fetchPriority={fetchPriority}
				{...(priority ? {} : { loading })}
				onLoad={onLoad}
			/>
		</div>
	);
});
